/**
 * @fileoverview Org portal — marketplace program listings. Publish programs
 * the org needs instructors for, control who can see each listing (all
 * instructors / org instructors / specific emails), review incoming
 * applications, and manage members.
 *
 * Staffing and reporting live on the program detail page (`/org/programs/:id`);
 * this page publishes and routes to it. Programs are square tiles in a grid
 * that fills the wide page column, four across a laptop screen. Each tile opens
 * its program and shows a chevron at rest — an underline-on-hover title was
 * invisible, so the detail page may as well not have existed. Creating and
 * editing a program happen in a dialog, which is also where a listing is marked
 * filled or reopened; archiving one stays a confirmed control on the tile, and
 * archiving, restoring and deleting live in the program's Settings.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, Button, Tag, IconButton, Icon, Eyebrow, SegmentedControl, PageShell } from "@/components/studio/primitives";
import TemplatePickerDialog, { serverParts, skippedLine } from "@/components/org/TemplatePickerDialog";

/* A two-way toggle. The stored model still understands the retired "org"
   value, and a program saved with it READS as private — collapsing three
   settings into two must never widen who can see a listing. */
export const VISIBILITY_OPTIONS = [
  { value: "public", label: "Public program", desc: "Any Hans instructor can see and apply." },
  { value: "invite", label: "Private program", desc: "Only the email addresses you list." },
];

export const PROGRAM_TYPE_OPTIONS = [
  { value: "private_lessons", label: "Private lessons", desc: "Families book an instructor directly." },
  { value: "class", label: "Class", desc: "The program runs classes you schedule." },
  { value: "camp", label: "Camp", desc: "Classes over a fixed run — an end date is required." },
];

const STATUS_LABELS = { open: "Open", filled: "Filled", closed: "Archived" };

/* Whether a listing is still recruiting. Archiving is a separate, confirmed
   action on the tile and in the program's Settings — it takes the program and
   its classes off the marketplace, the directory and the calendars — so it is
   not offered here. */
export const LISTING_STATUS_OPTIONS = [
  { value: "open", label: "Open", desc: "Taking applications from instructors." },
  { value: "filled", label: "Filled", desc: "Staffed — no longer taking applications." },
];

/** A retired visibility reads as private rather than as itself. */
export const visibilityValue = (v) => (v === "public" ? "public" : "invite");

/* What a new program can take from an existing one. `client` parts fill in
   the form below the picker, where they can still be changed; the rest are
   copied by the server once the program exists (services/program_templates). */
export const PROGRAM_TEMPLATE_PARTS = [
  { id: "title", label: "Title and description", desc: "Filled in below, ready to change.", client: true },
  { id: "type", label: "Program type", client: true },
  { id: "dates", label: "Start and end dates", desc: "Usually different for a new run.", client: true, defaultOn: false },
  { id: "visibility", label: "Who can see it", desc: "Public or private, and the invited emails.", client: true },
  { id: "brief", label: "What instructors need to know" },
  { id: "contacts", label: "Points of contact", desc: "The school's contacts and your admins." },
  { id: "listing", label: "Listing details", desc: "Instruments, location, pay and when it runs." },
  { id: "location", label: "Address and time zone", desc: "Where its classes meet." },
  { id: "emails", label: "Program emails", desc: "Weekly recap, week ahead, cancellation notices and who receives them." },
  { id: "substitutes", label: "Substitute requests", desc: "The program's own substitute rules." },
  { id: "files", label: "Files", desc: "The program's files, and each copied class's files and curriculum." },
  {
    id: "classes",
    label: "Classes",
    desc: "Each current class with its room, notes, meeting days and holidays, ready for you to schedule.",
  },
  {
    id: "instructors",
    label: "Instructors",
    desc: "Invited by email and added when they accept. A copied class keeps its instructor if they belong to your organization.",
  },
];

/** The create form, prefilled with the `client` parts of a template program. */
export function formFromTemplate(p, parts) {
  const has = (id) => parts.includes(id);
  const type = has("type") ? p.program_type || "private_lessons" : EMPTY_FORM.program_type;
  const dates = has("dates");
  return {
    ...EMPTY_FORM,
    ...(has("title") ? { title: p.title || "", description: p.description || "" } : {}),
    program_type: type,
    start_date: dates ? p.start_date || "" : "",
    end_date: dates ? p.end_date || "" : "",
    // A camp always ends; anything else runs indefinitely unless its dates came too.
    indefinite: type === "camp" ? false : !(dates && p.end_date),
    ...(has("visibility")
      ? { visibility: visibilityValue(p.visibility), invited_emails: (p.invited_emails || []).join("\n") }
      : {}),
  };
}

const EMPTY_FORM = {
  title: "",
  description: "",
  program_type: "private_lessons",
  start_date: "",
  end_date: "",
  indefinite: true,
  visibility: "invite",
  invited_emails: "",
};

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

const splitList = (raw, sep) =>
  raw
    .split(sep)
    .map((s) => s.trim())
    .filter(Boolean);

/** Build the create/update payload from the form state.

    The recruiting fields (instruments, location, compensation, when-it-runs,
    contact) are deliberately absent: they are no longer part of the intake and
    are filled in later from the program's own Settings, so a create must not
    send them and blank them on an edit. */
const toPayload = (form) => ({
  title: form.title.trim(),
  description: form.description.trim(),
  program_type: form.program_type,
  start_date: form.start_date || null,
  // Indefinite IS the absence of an end date — there is no separate flag to
  // fall out of step with it.
  end_date: form.indefinite ? null : form.end_date || null,
  visibility: form.visibility,
  invited_emails: form.visibility === "invite" ? splitList(form.invited_emails, /[\s,;]+/) : [],
  // Only an edit carries a status, and a closed program keeps being closed.
  ...(form.status === "open" || form.status === "filled" ? { status: form.status } : {}),
});

function ProgramForm({ initial, onSubmit, onCancel, submitLabel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Give the program a title");
      return;
    }
    if (form.program_type === "camp" && (form.indefinite || !form.end_date)) {
      toast.error("A camp runs for a fixed period — give it an end date");
      return;
    }
    if (!form.indefinite && form.end_date && form.start_date && form.end_date < form.start_date) {
      toast.error("The end date cannot come before the start date");
      return;
    }
    if (form.visibility === "invite" && splitList(form.invited_emails, /[\s,;]+/).length === 0) {
      toast.error("List at least one email address for a private program");
      return;
    }
    onSubmit(toPayload(form));
  };

  // A camp always ends, so it cannot be indefinite. Choosing it clears the
  // toggle rather than leaving a checked box the form would then reject.
  const setType = (value) =>
    setForm((f) => ({ ...f, program_type: value, indefinite: value === "camp" ? false : f.indefinite }));

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="program-form">
      <input
        value={form.title}
        onChange={set("title")}
        placeholder="Program title"
        data-testid="program-title"
        className={inputClass}
      />

      <textarea
        value={form.description}
        onChange={set("description")}
        placeholder="A sentence on what this program is"
        rows={2}
        maxLength={280}
        data-testid="program-description"
        className={inputClass}
      />

      <div>
        <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">Program type</div>
        <select
          value={form.program_type}
          onChange={(e) => setType(e.target.value)}
          aria-label="Program type"
          data-testid="program-type"
          className={inputClass}
        >
          {PROGRAM_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
          {PROGRAM_TYPE_OPTIONS.find((o) => o.value === form.program_type)?.desc}
        </p>
      </div>

      <div>
        <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">When it runs</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block font-sans text-[12px] text-[var(--text-tertiary)]">Starts</span>
            <input
              type="date"
              value={form.start_date}
              onChange={set("start_date")}
              aria-label="Start date"
              data-testid="program-start-date"
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="mb-1 block font-sans text-[12px] text-[var(--text-tertiary)]">Ends</span>
            <input
              type="date"
              value={form.indefinite ? "" : form.end_date}
              onChange={set("end_date")}
              disabled={form.indefinite}
              aria-label="End date"
              data-testid="program-end-date"
              className={`${inputClass} disabled:opacity-50`}
            />
          </label>
        </div>
        <label className="mt-2 flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={form.indefinite}
            disabled={form.program_type === "camp"}
            onChange={(e) => setForm((f) => ({ ...f, indefinite: e.target.checked }))}
            data-testid="program-indefinite"
            className="accent-[var(--accent-yellow)]"
          />
          <span className="font-sans text-[14px] text-[var(--text-secondary)]">
            Runs indefinitely
            {form.program_type === "camp" && (
              <span className="text-[var(--text-tertiary)]"> — a camp needs an end date</span>
            )}
          </span>
        </label>
      </div>

      <div>
        <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">Who can see this listing?</div>
        <div className="flex flex-col gap-2">
          {VISIBILITY_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex cursor-pointer items-start gap-3">
              <input
                type="radio"
                name="visibility"
                value={opt.value}
                checked={visibilityValue(form.visibility) === opt.value}
                onChange={set("visibility")}
                data-testid={`program-visibility-${opt.value}`}
                className="mt-1 accent-[var(--accent-yellow)]"
              />
              <span>
                <span className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{opt.label}</span>
                <span className="block font-sans text-[13px] text-[var(--text-tertiary)]">{opt.desc}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {visibilityValue(form.visibility) === "invite" && (
        <textarea
          value={form.invited_emails}
          onChange={set("invited_emails")}
          placeholder={"Invited emails — one per line or comma-separated\nteacher@email.com"}
          rows={3}
          data-testid="program-invited-emails"
          className={inputClass}
        />
      )}

      {(form.status === "open" || form.status === "filled") && (
        <div>
          <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">Listing status</div>
          <div className="flex flex-col gap-2">
            {LISTING_STATUS_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex cursor-pointer items-start gap-3">
                <input
                  type="radio"
                  name="status"
                  value={opt.value}
                  checked={form.status === opt.value}
                  onChange={set("status")}
                  data-testid={`program-status-${opt.value}`}
                  className="mt-1 accent-[var(--accent-yellow)]"
                />
                <span>
                  <span className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{opt.label}</span>
                  <span className="block font-sans text-[13px] text-[var(--text-tertiary)]">{opt.desc}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <p className="font-sans text-[12px] text-[var(--text-tertiary)]">
        Instruments, location, pay and a contact are optional and live in the program&rsquo;s Settings once it
        exists. Anything you leave blank is simply left off the listing.
      </p>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={saving} data-testid="program-submit">
          {saving ? "Saving…" : submitLabel}
        </Button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

/** "Sep 8, 2026" from an ISO day, read as that calendar day wherever the
    browser is — `new Date("2026-09-08")` is UTC midnight and shows the 7th
    west of Greenwich. */
export function formatDay(iso) {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Today as an ISO day in the browser's own calendar, the one a date input writes. */
export function localToday(now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Whole days from one ISO day to another, counted on the calendar so a
    daylight-saving change cannot make a week 6.96 days long. */
function daysBetween(fromIso, toIso) {
  const at = (iso) => {
    const [y, m, d] = String(iso).split("-").map(Number);
    return Date.UTC(y, (m || 1) - 1, d || 1);
  };
  return Math.round((at(toIso) - at(fromIso)) / 86400000);
}

const weeks = (n) => `${n} week${n === 1 ? "" : "s"}`;

/**
 * Where a program is in its run, in a few words, or null when nothing honest
 * can be said.
 *
 * With an end date, the weeks left, counting the final partial week as one so
 * a program in its last days reads "1 week remaining" rather than "0". With no
 * end date there is nothing to count down to, so it counts up: whole weeks
 * since it began. A program that has not begun says when it will; one whose
 * end date has passed says it has ended. An archived one has no line: its
 * status says it.
 *
 * @param {{start_date?: string, end_date?: string, status?: string}} p
 * @param {string} [today] - ISO day; the browser's today when left out.
 * @returns {string|null}
 */
export function programProgress(p, today = localToday()) {
  const start = p.start_date || null;
  const end = p.end_date || null;
  // Archived: the tile's status tag already says so, and a run that was
  // stopped has no weeks left or passed worth counting.
  if (p.status === "closed") return null;
  if (end && end < today) return "Ended";
  if (start && start > today) return `Starts in ${weeks(Math.ceil(daysBetween(today, start) / 7))}`;
  if (end) return `${weeks(Math.ceil((daysBetween(today, end) + 1) / 7))} remaining`;
  if (!start) return null;
  const passed = Math.floor(daysBetween(start, today) / 7);
  return passed === 0 ? "Started this week" : `${weeks(passed)} since start`;
}

function Fact({ icon, children, className, ...rest }) {
  return (
    <div className={`flex min-w-0 items-center gap-2 ${className || ""}`} {...rest}>
      <Icon name={icon} size={14} strokeWidth={1.8} className="shrink-0 text-[var(--text-tertiary)]" />
      <span className="truncate">{children}</span>
    </div>
  );
}

/**
 * One program as a square tile, sized so four sit across a laptop screen. In a
 * grid too narrow for two across it is an ordinary card of its own height.
 *
 * The tile is the target — clicking it opens the program, which is what people
 * try first — and the title is a real button so the way in is reachable from
 * the keyboard. The edit and archive controls stop the click from reaching it.
 * What a tile has to hold is budgeted against its smallest size: the title and
 * description clamp at two lines each, and the rest is one line apiece.
 */
function ProgramTile({ program: p, onOpen, onEdit, onArchive }) {
  const type = PROGRAM_TYPE_OPTIONS.find((o) => o.value === (p.program_type || "private_lessons"))?.label;
  const isPublic = visibilityValue(p.visibility) === "public";
  const count = (p.instructor_ids || []).length;
  const progress = programProgress(p);
  const stop = (e) => e.stopPropagation();

  return (
    <Card
      data-testid={`program-${p.program_id}`}
      onPress={onOpen}
      hover
      padding={16}
      className="flex flex-col overflow-hidden @min-[536px]:aspect-square"
    >
      <div className="flex items-center justify-between gap-2">
        <Eyebrow>{type}</Eyebrow>
        <div className="flex shrink-0 items-center gap-2">
          <span className="flex items-center gap-1 font-sans text-[12px] font-medium text-[var(--text-tertiary)]">
            <Icon name={isPublic ? "eye" : "lock"} size={13} strokeWidth={1.8} />
            {isPublic ? "Public" : "Private"}
          </span>
          {/* The at-rest sign that the tile opens — the chevron a pressable
              StatCard wears. A hover state alone left the detail page unfound. */}
          <Icon name="chevronRight" size={16} aria-hidden="true" className="text-[var(--text-tertiary)]" />
        </div>
      </div>

      <button
        type="button"
        onClick={(e) => { stop(e); onOpen(); }}
        data-testid={`program-open-${p.program_id}`}
        className="mt-3 line-clamp-2 shrink-0 text-left font-sans text-[17px] font-semibold leading-[22px] tracking-[-0.01em] text-[var(--text-primary)]"
      >
        {p.title}
      </button>
      {p.description && (
        <p className="mt-1 line-clamp-2 shrink-0 font-sans text-[13.5px] leading-[19px] text-[var(--text-secondary)]">
          {p.description}
        </p>
      )}

      {/* Where the program is in its run, then who teaches it across from
          when it began: the count on the left, the start date on the right. */}
      <div className="mt-auto flex flex-col gap-[5px] pt-4 font-sans text-[13px] leading-[18px] text-[var(--text-tertiary)] @min-[536px]:pt-2">
        {progress && (
          <Fact icon="hourglass" data-testid={`program-progress-${p.program_id}`}>
            {progress}
          </Fact>
        )}
        <div className="flex items-center justify-between gap-3">
          <Fact icon="users">{`${count} instructor${count === 1 ? "" : "s"}`}</Fact>
          <span
            className="flex min-w-0 shrink-0 items-center gap-1.5"
            title={p.start_date ? `Starts ${formatDay(p.start_date)}` : "No start date set"}
            data-testid={`program-start-${p.program_id}`}
          >
            <Icon name="calendar" size={14} strokeWidth={1.8} className="shrink-0" />
            <span className="truncate">{p.start_date ? formatDay(p.start_date) : "No start date"}</span>
          </span>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-[var(--studio-divider)] pt-3" onClick={stop}>
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {/* Neutral, so yellow on a tile means one thing: an application is
              waiting on the org. */}
          <Tag>{STATUS_LABELS[p.status] || p.status}</Tag>
          {p.pending_applications > 0 && <Tag accent>{p.pending_applications} pending</Tag>}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <IconButton icon="edit" size={30} ariaLabel={`Edit ${p.title}`} onPress={onEdit} data-testid={`program-edit-${p.program_id}`} />
          {p.status !== "closed" && (
            <IconButton
              icon="archive"
              size={30}
              ariaLabel={`Archive ${p.title}`}
              onPress={onArchive}
              data-testid={`program-archive-${p.program_id}`}
            />
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * Staffing a program: find instructors who are open to work, pick several, and
 * invite them in one go — plus invite by email for anyone not on Hans yet.
 *
 * The directory is opt-in on the instructor's side and carries no email
 * address, so inviting is the only way to reach someone from here. That is
 * deliberate: it keeps a record of who was approached and leaves the decision
 * with the instructor.
 */
export default function OrgProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [tab, setTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await orgApi.get("/org/programs");
      setPrograms(res.data.programs || []);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't load programs"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* Starting from a template: the form is prefilled (and remounted, since it
     holds its own state), and the server parts ride the create. */
  const [picking, setPicking] = useState(false);
  const [template, setTemplate] = useState(null); // {program_id, name, parts, chosen}
  const [createInitial, setCreateInitial] = useState(EMPTY_FORM);
  const [formKey, setFormKey] = useState(0);

  const openCreate = () => {
    setTemplate(null);
    setCreateInitial(EMPTY_FORM);
    setFormKey((k) => k + 1);
    setCreating(true);
  };

  const applyTemplate = ({ source, parts }) => {
    const p = programs.find((x) => x.program_id === source.id);
    if (!p) return;
    setTemplate({ program_id: p.program_id, name: p.title, parts: serverParts(PROGRAM_TEMPLATE_PARTS, parts), chosen: parts });
    setCreateInitial(formFromTemplate(p, parts));
    setFormKey((k) => k + 1);
  };

  const create = async (payload) => {
    setSaving(true);
    try {
      const body = template?.parts.length ? { ...payload, template: { program_id: template.program_id, parts: template.parts } } : payload;
      const res = await orgApi.post("/org/programs", body);
      const summary = res.data?.template;
      if (summary) {
        toast.success(`Program created from ${template.name}`, { description: skippedLine(summary) });
      } else {
        toast.success("Program published");
      }
      setCreating(false);
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't publish the program"));
    } finally {
      setSaving(false);
    }
  };

  const templateSources = useMemo(
    () =>
      programs.map((p) => ({
        id: p.program_id,
        name: p.title,
        hint: p.lifecycle === "past" ? "Past" : undefined,
      })),
    [programs],
  );

  /* The whole form goes up; the server drops what is unchanged before it
     tells the program's instructors what was updated. */
  const update = async (programId, payload) => {
    setSaving(true);
    try {
      await orgApi.put(`/org/programs/${programId}`, payload);
      toast.success("Program updated");
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't update the program"));
    } finally {
      setSaving(false);
    }
  };

  /* The same archive the program's Settings offers: its classes' meetings
     from today on are cancelled and it moves to Past. Deleting lives only in
     Settings, behind its warning. */
  const archive = async (program) => {
    if (
      !window.confirm(
        `Archive "${program.title}"?\n\nEvery class's meetings from today on are cancelled, and it leaves the marketplace and moves to Past. Everything is kept, and you can restore it from its Settings.`,
      )
    )
      return;
    try {
      await orgApi.post(`/org/programs/${program.program_id}/archive`);
      toast.success("Archived");
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't archive the program"));
    }
  };

  const editForm = (p) => ({
    title: p.title || "",
    description: p.description || "",
    program_type: p.program_type || "private_lessons",
    start_date: p.start_date || "",
    end_date: p.end_date || "",
    indefinite: !p.end_date,
    // A program stored as the retired "org" visibility edits as private.
    visibility: visibilityValue(p.visibility),
    invited_emails: (p.invited_emails || []).join("\n"),
    status: p.status || "open",
  });

  /* Two lists rather than one long one: an org running a handful of programs a
     term accumulates finished ones fast, and a past program is reference, not
     something you act on. The split rule lives on the server (`lifecycle`) so
     this tab and the emails cannot disagree about what "still running" means. */
  const active = programs.filter((p) => (p.lifecycle || "active") === "active");
  const past = programs.filter((p) => p.lifecycle === "past");
  const shown = tab === "active" ? active : past;

  const editing = programs.find((p) => p.program_id === editingId);

  return (
    <PageShell width="wide">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="mb-2 font-serif text-2xl font-semibold text-[var(--text-primary)]">Programs</h1>
          <p className="font-sans text-[14px] text-[var(--text-tertiary)]">
            Each program holds a set of classes or lessons: its instructors, students and schedule. Instructors join by
            invitation, or apply when it&rsquo;s public.
          </p>
        </div>
        <Button icon="plus" onPress={openCreate} data-testid="program-new">
          New program
        </Button>
      </div>

      {!loading && programs.length > 0 && (
        <div className="mb-5">
          <SegmentedControl
            testId="program-tabs"
            value={tab}
            onChange={setTab}
            options={[
              { id: "active", label: "Active", count: active.length },
              { id: "past", label: "Past", count: past.length },
            ]}
          />
        </div>
      )}

      {loading ? (
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>
      ) : programs.length === 0 ? (
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">No programs yet. Make one for each set of classes or lessons you run.</p>
      ) : shown.length === 0 ? (
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">
          {tab === "active" ? "Nothing running right now." : "No past programs yet."}
        </p>
      ) : (
        /* auto-fill rather than fixed columns: the tile keeps its size and the
           row gains or loses a tile as the window does, so a wide screen fills
           with programs instead of margin. A tile is square only once two fit
           across (2 × 260px + the gap) — one square filling a phone or a narrow
           tablet column is mostly empty middle. */
        <div className="@container">
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(260px,1fr))]" data-testid="program-grid">
            {shown.map((p) => (
              <ProgramTile
                key={p.program_id}
                program={p}
                onOpen={() => navigate(`/org/programs/${p.program_id}`)}
                onEdit={() => setEditingId(p.program_id)}
                onArchive={() => archive(p)}
              />
            ))}
          </div>
        </div>
      )}

      <Dialog open={creating} onOpenChange={(open) => !open && setCreating(false)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-semibold">New program</DialogTitle>
          </DialogHeader>
          {/* Start from an existing program, or carry on from scratch. */}
          {programs.length > 0 &&
            (template ? (
              <div
                className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] px-4 py-3"
                data-testid="program-template-chosen"
              >
                <span className="flex min-w-0 items-center gap-2 font-sans text-[14px] text-[var(--text-secondary)]">
                  <Icon name="copy" size={15} className="shrink-0 text-[var(--text-tertiary)]" />
                  <span className="min-w-0">
                    From <span className="font-medium text-[var(--text-primary)]">{template.name}</span>
                    {template.parts.length > 0 && ` · ${template.parts.length} more copied when you publish`}
                  </span>
                </span>
                <span className="flex items-center gap-3">
                  <button type="button" onClick={() => setPicking(true)} data-testid="program-template-change" className="font-sans text-[13px] font-medium text-[var(--accent-yellow-text)] hover:underline">
                    Change
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTemplate(null); setCreateInitial(EMPTY_FORM); setFormKey((k) => k + 1); }}
                    data-testid="program-template-clear"
                    className="font-sans text-[13px] text-[var(--text-tertiary)] hover:underline"
                  >
                    Start blank
                  </button>
                </span>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="ghost" size="sm" icon="copy" onPress={() => setPicking(true)} data-testid="program-template-open">
                  Start from a template
                </Button>
                <span className="font-sans text-[13px] text-[var(--text-tertiary)]">
                  Copy its settings, classes, contacts and more.
                </span>
              </div>
            ))}
          <ProgramForm
            key={formKey}
            initial={createInitial}
            onSubmit={create}
            onCancel={() => setCreating(false)}
            submitLabel="Create program"
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      <TemplatePickerDialog
        open={picking}
        onOpenChange={setPicking}
        title="Start from a template"
        description="Choose a program and what to bring into the new one. Nothing is saved until you publish."
        sourceLabel="Program"
        sources={templateSources}
        parts={PROGRAM_TEMPLATE_PARTS}
        initialSource={template?.program_id}
        initialParts={template?.chosen}
        onApply={applyTemplate}
        testId="program-template"
      />

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditingId(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-semibold">Edit program</DialogTitle>
          </DialogHeader>
          {editing && (
            <ProgramForm
              initial={editForm(editing)}
              onSubmit={(payload) => update(editing.program_id, payload)}
              onCancel={() => setEditingId(null)}
              submitLabel="Save changes"
              saving={saving}
            />
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
